import { Popover, Stack, Text, Table, Divider } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useTimePeriod } from "../../../../../utils/use-time-period"
import dayjs from "dayjs"
import { SuspenseWithErrorBoundary } from "../../../../../-components/suspense"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import app_serverResources from "../../../../../@resource-interfaces/app_serverResources"
import { ReactNode } from "react"

export function ActivityPopover({
  activityId,
  departmentId,
  children,
}: {
  departmentId: number
  activityId: number
  children: React.ReactNode
}) {
  return (
    <Popover shadow="md" withArrow>
      <Popover.Dropdown>
        <SuspenseWithErrorBoundary>
          <Component activityId={activityId} departmentId={departmentId} />
        </SuspenseWithErrorBoundary>
      </Popover.Dropdown>
      <Popover.Target>{children}</Popover.Target>
    </Popover>
  )
}

type LanguageKey = keyof app_serverResources

function Component({
  activityId,
  departmentId,
}: {
  activityId: number
  departmentId: number
}) {
  const { getTimePeriod } = useTimePeriod()
  const { data: activity } = useSuspenseQueryGet(
    "/api/Orchestra/GetOrchestraActivity",
    {
      activityId,
      departmentId,
    }
  )
  const properties: {
    key: LanguageKey | ReactNode
    value: ReactNode
  }[] = [
    { key: "Title", value: activity.title },
    { key: "Stage", value: activity.stage },
    {
      key: "Date",
      value: dayjs(activity.date).format("ddd LL"),
    },
    ...(activity.timePeriod && activity.timePeriod.start
      ? [
          {
            key: "Time" as LanguageKey,
            value: getTimePeriod(activity.timePeriod),
          },
        ]
      : []),

    ...activity.collectivePresencePeriods.map((v) => ({
      key: v.collectivePersonName,
      value: getTimePeriod(v.period),
    })),
    ...activity.parts.map((v) => ({
      key: (
        <Text fw={500}>
          {v.start.substring(0, 5)}-{v.end.substring(0, 5)}
        </Text>
      ),
      value: (
        <div>
          {v.actParts.map((v) => (
            <Text>{v.displayValue}</Text>
          ))}
          {v.productionParts.map((v) => (
            <Text>{v.displayValue}</Text>
          ))}
          {v.sceneParts.map((v) => (
            <Text>{v.displayValue}</Text>
          ))}
          {v.completeParts.map((v) => (
            <Text>{v.displayValue}</Text>
          ))}
        </div>
      ),
    })),
  ]
  const { t } = useTranslation("app_server")

  return (
    <Stack>
      <Table withRowBorders>
        <Table.Tbody>
          {properties.map((prop, i) => (
            <Table.Tr key={i}>
              <Table.Td style={{ verticalAlign: "baseline" }}>
                {typeof prop.key === "string" ? (
                  <Text fw={500}>{t(prop.key as any)}</Text>
                ) : (
                  prop.key
                )}
              </Table.Td>
              <Table.Td>
                {typeof prop.value === "string" ? (
                  <Text>{prop.value}</Text>
                ) : (
                  prop.value
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {activity.departmentNote ? (
        <Note label={t("DepartmentNote")} value={activity.departmentNote} />
      ) : null}

      {activity.artisticNote ? (
        <Note label={t("ArtisticNote")} value={activity.artisticNote} />
      ) : null}

      {/* <Divider /> */}
      {/* <Link */}
      {/*   to="/app/activities/$id" */}
      {/*   params={{ id: activityId }} */}
      {/*   style={{ textDecoration: "unset" }} */}
      {/* > */}
      {/*   <Button fullWidth variant="default"> */}
      {/*     {t("ShowActivity")} */}
      {/*   </Button> */}
      {/* </Link> */}
    </Stack>
  )
}

function Note({ label, value }: { label: string; value: string }) {
  return (
    <>
      <Divider />
      <Text fw={500} span>
        {label}
      </Text>
      {typeof value === "string" ? <Text>{value}</Text> : value}
    </>
  )
}
